import logger from '@eventbrite/client-logger';
import { ProgressIndicator } from '@eventbrite/eds-progress-indicator';
import {
    AuthorizationData,
    ButtonColorOption,
    ButtonLabelOption,
    ButtonShapeOption,
    ButtonSizeOption,
    ButtonStyle,
    Environment,
    FlowType,
    Intent,
} from '@eventbrite/paypal-checkout-types';
import classNames from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';
import { PaypalBuyerDetails } from '../../../../models/paymentMethods/paypal';
import { PaypalScriptStatus } from '../../../../state/paypal';
import { getPaypalCheckoutInstance } from '../../../../utils/paypalScriptLoader';
import './PaypalButton.scss';

const windowWithPaypal = global.window as unknown as {
    paypal?: {
        Buttons: {
            driver: (framework: string, config: any) => typeof React.Component;
        };
    };
};

enum PaypalLayout {
    vertical = 'vertical',
    horizontal = 'horizontal',
}

interface PaypalButtonStyle extends ButtonStyle {
    height: number;
    layout: PaypalLayout;
}

const PAYPAL_BUTTON_HEIGHT_IN_PIXELS = 44;

// PayPal Checkout Button documentation can be checked in: https://developer.paypal.com/docs/checkout/how-to/customize-button/
const getPaypalButtonStyle = (color: ButtonColorOption): PaypalButtonStyle => ({
    color,
    label: ButtonLabelOption.PayPal,
    shape: ButtonShapeOption.Rect,
    size: ButtonSizeOption.Responsive,
    tagline: false,
    layout: PaypalLayout.horizontal,
    height: PAYPAL_BUTTON_HEIGHT_IN_PIXELS,
});

export interface PaypalButtonNonCallbackProps {
    amountTotal: string;
    amountCurrency: string;
    color?: ButtonColorOption;
    env: Environment;
    paypalScriptStatus: PaypalScriptStatus;
    isCheckoutButton?: Boolean;
}

export interface PaypalButtonCallbackProps {
    onPaypalAuthorize: (buyerDetails: PaypalBuyerDetails) => void;
    onPaypalCancel?: () => void;
    onPaypalClick?: (data: { fundingSource: string }) => void;
    onPaypalError: (error: Error) => void;
    onPaypalInit?: () => void;
}

export interface PaypalButtonProps
    extends PaypalButtonNonCallbackProps,
        PaypalButtonCallbackProps {}

export const PaypalButton: React.FunctionComponent<PaypalButtonProps> = ({
    amountCurrency,
    amountTotal,
    color = ButtonColorOption.Blue,
    env,
    onPaypalAuthorize,
    onPaypalCancel = () => undefined,
    onPaypalClick,
    onPaypalError,
    onPaypalInit,
    paypalScriptStatus,
    isCheckoutButton = false,
}) => {
    // /!\ WARNING _createPaypalPayment MUST return a string: this string is the orderId used by Paypal internally
    const createPaypalPayment: () => Promise<string> = async () => {
        const checkoutInstance = await getPaypalCheckoutInstance();

        if (!checkoutInstance) {
            throw new Error(
                '[EB-152450] Paypal checkout instance creation failed',
            );
        }

        return checkoutInstance.createPayment({
            flow: FlowType.Checkout,
            amount: amountTotal,
            currency: amountCurrency,
            intent: Intent.Authorize,
        });
    };

    const onAuthorizeHandler = async (data: AuthorizationData) => {
        const checkoutInstance = await getPaypalCheckoutInstance();

        if (!checkoutInstance) {
            throw new Error(
                '[EB-152450] Paypal checkout instance creation failed',
            );
        }

        const {
            nonce,
            details: { creditFinancingOffered, email, firstName, lastName },
        } = await checkoutInstance.tokenizePayment(data);

        // Save the nonce, the user's email and the settings of credit in redux.
        onPaypalAuthorize({
            credit: creditFinancingOffered !== undefined,
            email,
            firstName,
            lastName,
            nonce,
        });
    };

    const onPaypalErrorHandler = (error: Error) => {
        logger.error('[EB-152450] Paypal button raised an error', {
            error,
        });
        onPaypalError(error);
    };

    const paypalButtonClassName = classNames({
        'react-paypal-button': true,
        'react-paypal-button--modal-footer': isCheckoutButton,
    });

    const paypalWrapperClassName = classNames({
        'react-paypal-wrapper': isCheckoutButton,
    });

    if (
        !windowWithPaypal.paypal ||
        paypalScriptStatus !== PaypalScriptStatus.loaded
    ) {
        return (
            <ProgressIndicator
                data-spec="paypal-button-loading-indicator"
                style="gradient"
                size="small-chunky"
                shape="circular"
            />
        );
    }

    try {
        const PaypalButtonComponent = windowWithPaypal.paypal.Buttons.driver(
            'react',
            {
                React,
                ReactDOM,
            },
        );

        return (
            <div className={paypalWrapperClassName}>
                <div
                    className={paypalButtonClassName}
                    data-spec="react-paypal-button"
                >
                    <PaypalButtonComponent
                        commit={false}
                        createOrder={createPaypalPayment}
                        data-spec="paypal-button-component"
                        env={env}
                        onApprove={onAuthorizeHandler}
                        onCancel={onPaypalCancel}
                        onClick={onPaypalClick}
                        onError={onPaypalErrorHandler}
                        onInit={onPaypalInit}
                        style={getPaypalButtonStyle(color)}
                    />
                </div>
            </div>
        );
    } catch (e) {
        logger.error(e);
        return null;
    }
};
